import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useAgentRun } from "../useAgentRun";
import {
  ACTIVE_CONVERSATION_QUERY_KEY,
  newClientMessageId,
} from "./constants";

/**
 * Owns one chat send → SSE stream → optimistic write lifecycle.
 *
 * Phase 5 of plans/useChatHook-enterprise-grade.md moved the
 * `isStreaming` flag out of this hook and into the chat reducer. The
 * hook now dispatches lifecycle transitions (STREAM_START,
 * STREAM_COMPLETE, STREAM_ERROR, STREAM_CANCELLED) and lets the
 * reducer hold the truth. Local state would just shadow the reducer
 * and could disagree.
 *
 * The hook also installs an unmount cleanup that cancels any in-flight
 * run, so an SSE stream cannot outlive the component that started it.
 *
 * Returns `{ sendMessage, cancelStreaming }`.
 *
 * `sendMessage(conversationId, content)` resolves to:
 *   { ok, error?, runId, conversation }
 * where `conversation` is the latest cache snapshot at completion —
 * the persistence layer can pass that straight to the store endpoint
 * without re-reading the cache.
 */
export function useSendMessage({ clientId, dispatch }) {
  const queryClient = useQueryClient();

  // Ref pattern: the agentRun loop calls onUpdate per SSE event, and we
  // want each send to install its own refreshCache without re-creating
  // the agentRun callbacks (which would cancel the in-flight subscribe).
  const agentRunCallbacksRef = useRef({ onUpdate: () => {} });
  const agentRun = useAgentRun({
    onUpdate: (...args) => agentRunCallbacksRef.current.onUpdate(...args),
  });

  const sendMessage = useCallback(
    async (conversationId, content) => {
      const contentToSend = (content || "").trim();
      if (!contentToSend || !conversationId) {
        return { ok: false, error: { kind: "skipped" } };
      }

      // Cancel any in-flight queries to prevent a background refetch
      // from overwriting the streaming placeholder.
      await queryClient.cancelQueries({
        queryKey: [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
      });

      dispatch({ type: "STREAM_START" });

      // One round-trip ID per send; user + assistant placeholders share
      // the prefix so they're easy to correlate but distinct as cache
      // keys. The IDs never leave the client — they're stripped by the
      // server-side conversation projection.
      const sendId = newClientMessageId();
      const assistantMessage = {
        role: "assistant",
        content: "",
        thinking: true,
        clientMessageId: `${sendId}:assistant`,
      };
      const userMessage = {
        role: "user",
        content: contentToSend,
        clientMessageId: `${sendId}:user`,
      };

      // Optimistic write: append the user's message and a placeholder
      // for the assistant. Important: store a *snapshot* of
      // assistantMessage rather than the live reference. React Query's
      // structural sharing (replaceEqualDeep) compares the new value to
      // the previous one and reuses the previous reference when they're
      // deeply equal — if the live `assistantMessage` were stored, every
      // refreshCache write would compare a fresh clone against the same
      // live object (now further mutated to be deeply equal to the
      // clone) and structural sharing would discard our clone, leaving
      // the cache pinned to the live reference forever. Storing a
      // snapshot here decouples the cache from the live mutation target.
      queryClient.setQueryData(
        [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
        (prev) => ({
          ...(prev || {}),
          id: conversationId,
          // A new send supersedes any earlier failure: clear the flag so
          // the regenerate banner goes away and a successful run persists
          // `false` via the store endpoint.
          networkErrorActive: false,
          messages: [
            ...(prev?.messages ?? []),
            userMessage,
            { ...assistantMessage },
          ],
        }),
      );

      const refreshCache = () => {
        // Locate the assistant placeholder by clientMessageId rather
        // than by position, then shallow-clone it so each tick produces
        // a *new* message reference. `useAgentRun` still mutates the
        // live `assistantMessage` object in place; the spread snapshots
        // its current state. If a second run displaced this placeholder
        // (clientMessageId no longer present in the cache), we drop the
        // write — late events from a stale run can no longer corrupt a
        // newer message's cache entry.
        queryClient.setQueryData(
          [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
          (prev) => {
            if (!prev) return prev;
            const idx = prev.messages.findIndex(
              (m) => m.clientMessageId === assistantMessage.clientMessageId,
            );
            if (idx === -1) return prev;
            const next = prev.messages.slice();
            next[idx] = { ...assistantMessage };
            return { ...prev, id: conversationId, messages: next };
          },
        );
      };

      // The server emits the persisted user-row id on a `user_message`
      // event. Stamp it onto the optimistic user message (located by
      // clientMessageId) so feedback can address that row after a round-trip.
      const setUserMessageId = (serverId) => {
        if (serverId == null) return;
        queryClient.setQueryData(
          [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
          (prev) => {
            if (!prev) return prev;
            const idx = prev.messages.findIndex(
              (m) => m.clientMessageId === userMessage.clientMessageId,
            );
            if (idx === -1) return prev;
            const next = prev.messages.slice();
            next[idx] = { ...next[idx], id: serverId };
            return { ...prev, id: conversationId, messages: next };
          },
        );
      };

      const markNetworkError = () => {
        queryClient.setQueryData(
          [ACTIVE_CONVERSATION_QUERY_KEY, conversationId],
          (prev) => ({ ...(prev || {}), networkErrorActive: true }),
        );
      };

      // Wire per-event cache refresh so the UI streams smoothly. Intercept
      // the `user_message` event to capture the persisted user-row id.
      agentRunCallbacksRef.current.onUpdate = (_assistantMessage, evt) => {
        if (evt?.event === "user_message") {
          setUserMessageId(evt.data?.message_id);
        }
        refreshCache();
      };
      const result = await agentRun.start({
        clientId,
        conversationId,
        message: contentToSend,
        assistantMessage,
      });
      agentRunCallbacksRef.current.onUpdate = () => {};
      // Final flush so any post-final mutations (e.g. `delete steps` in
      // useAgentRun's finally) reach the cache.
      refreshCache();

      // Translate the agentRun result into the reducer's vocabulary.
      if (result.ok) {
        dispatch({ type: "STREAM_COMPLETE" });
      } else if (result.error?.kind === "cancelled") {
        dispatch({ type: "STREAM_CANCELLED" });
      } else {
        markNetworkError();
        dispatch({
          type: "STREAM_ERROR",
          message: result.error?.message || result.error?.kind || "Stream failed",
        });
      }

      // Return the post-stream cache state so the caller's persistence
      // hook doesn't have to re-read it.
      const conversation = queryClient.getQueryData([
        ACTIVE_CONVERSATION_QUERY_KEY,
        conversationId,
      ]);

      return { ...result, conversation };
    },
    [clientId, queryClient, agentRun, dispatch],
  );

  const cancelStreaming = useCallback(async () => {
    // STREAM_CANCELLED is dispatched by sendMessage when the awaited
    // result returns with kind:"cancelled" — no local state to flip.
    await agentRun.cancel();
  }, [agentRun]);

  // Cancel any in-flight run on unmount so the SSE iterator and abort
  // controller don't outlive the component (e.g. when the user
  // navigates away mid-stream). `cancel` is a stable useCallback in
  // useAgentRun, so destructuring gives a referentially stable dep —
  // the wrapping `agentRun` object is not.
  const { cancel: cancelAgentRun } = agentRun;
  useEffect(() => {
    return () => {
      cancelAgentRun();
    };
  }, [cancelAgentRun]);

  return { sendMessage, cancelStreaming };
}
